// @spec GHG-STR-012
/**
 * React hook for polling GHG OCR processing status
 */

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useOrganizationId } from '@/hooks/use-organization-id';

import type {
  GhgOcrConfirmRequest,
  GhgOcrConfirmResponse,
  GhgOcrRetryResponse,
} from '../types/ocr';

import { confirmGhgOcrData, fetchGhgOcrStatus, retryGhgOcr } from './ocr';

/**
 * Query key factory for GHG OCR queries
 */
export const ghgOcrKeys = {
  all: ['ghg-ocr'] as const,
  status: (orgId: string, uploadId: string) =>
    [...ghgOcrKeys.all, 'status', orgId, uploadId] as const,
};

/**
 * Hook to fetch and poll OCR processing status.
 *
 * Automatically polls every 2 seconds while status is 'pending' or 'processing'.
 * Stops polling when status is 'completed' or 'failed'.
 */
export function useGhgOcrStatus(uploadId: string | undefined) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useOrganizationId();

  const query = useQuery({
    queryKey: ghgOcrKeys.status(organizationId ?? '', uploadId ?? ''),
    queryFn: async () => {
      if (!organizationId || !uploadId) {
        throw new Error('Organization or upload ID not found');
      }
      return fetchGhgOcrStatus(getToken, organizationId, uploadId);
    },
    enabled: (isSignedIn ?? false) && !!organizationId && !!uploadId,
    staleTime: 1000, // 1 second
    refetchInterval: (query) => {
      const data = query.state.data;
      // Poll every 2 seconds while processing
      if (data?.ocrStatus === 'pending' || data?.ocrStatus === 'processing') {
        return 2000;
      }
      // Stop polling when completed or failed
      return false;
    },
  });

  return {
    status: query.data?.ocrStatus,
    extractedData: query.data?.extractedData,
    processingError: query.data?.processingError,
    processedAt: query.data?.processedAt,
    isLoading: query.isLoading,
    isPolling: query.data?.ocrStatus === 'pending' || query.data?.ocrStatus === 'processing',
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to confirm OCR extracted data and create activity data entry.
 */
export function useConfirmGhgOcr(uploadId: string) {
  const { getToken } = useAuth();
  const organizationId = useOrganizationId();
  const queryClient = useQueryClient();

  return useMutation<GhgOcrConfirmResponse, Error, GhgOcrConfirmRequest>({
    mutationFn: async (data) => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return confirmGhgOcrData(getToken, organizationId, uploadId, data);
    },
    onSuccess: () => {
      // Invalidate related queries
      void queryClient.invalidateQueries({
        queryKey: ghgOcrKeys.all,
      });
      // Also invalidate activity data queries if they exist
      void queryClient.invalidateQueries({
        queryKey: ['ghg-activity-data'],
      });
    },
  });
}

/**
 * Hook to retry failed OCR processing.
 */
export function useRetryGhgOcr(uploadId: string) {
  const { getToken } = useAuth();
  const organizationId = useOrganizationId();
  const queryClient = useQueryClient();

  return useMutation<GhgOcrRetryResponse>({
    mutationFn: async () => {
      if (!organizationId) {
        throw new Error('Organization not found');
      }
      return retryGhgOcr(getToken, organizationId, uploadId);
    },
    onSuccess: () => {
      // Invalidate status to trigger re-polling
      void queryClient.invalidateQueries({
        queryKey: ghgOcrKeys.status(organizationId ?? '', uploadId),
      });
    },
  });
}
